import { api } from "../../../../services/api";

/**
 * Dados do navegador exigidos pelo EMV 3DS no enrollment. Obrigatórios para Mastercard — a
 * bandeira mais frequente nas nossas recusas — e recomendados nas demais, porque a doc do
 * PicPay registra que enviá-los aumenta a taxa de aprovação.
 *
 * Faltam dois dos dez campos do contrato: `httpAcceptBrowserValue` e `httpAcceptContent`. O
 * header Accept não é exposto ao JavaScript, então o backend os lê do header da requisição que
 * recebe daqui. É o que dá para fazer.
 *
 * Todos os valores vão como string porque é assim que o contrato os declara, com exceção de
 * `javaEnabled`, que é boolean.
 */
export function collectBrowserData() {
  const screen = typeof window !== "undefined" ? window.screen : null;
  let javaEnabled = false;
  try {
    javaEnabled = typeof navigator.javaEnabled === "function" ? navigator.javaEnabled() : false;
  } catch (_) {
    // navigator.javaEnabled() é obsoleto e pode lançar em alguns navegadores; false é a
    // resposta correta em todos eles hoje.
  }

  return {
    language: navigator.language || "pt-BR",
    javaEnabled,
    colorDepth: String(screen?.colorDepth ?? 24),
    screenHeight: String(screen?.height ?? 0),
    screenWidth: String(screen?.width ?? 0),
    timeDifference: String(new Date().getTimezoneOffset()),
    userAgent: navigator.userAgent || "",
  };
}

/**
 * Acompanha o desfecho do desafio consultando o backend, que por sua vez consulta o PicPay.
 *
 * É o mecanismo principal, não um fallback. O PicPay não documenta contrato de postMessage para
 * o StepUp em nenhuma das suas páginas, e nunca observamos um — nenhum desafio chegou a ocorrer
 * desde que este código existe, e o sandbox não renderiza desafio. Depender de uma mensagem cujo
 * formato não conhecemos seria repetir o defeito que manteve a coleta de dados de dispositivo
 * quebrada por meses.
 *
 * Falha de rede não encerra a espera: só o teto de tempo decide. Uma instabilidade momentânea
 * não deve derrubar uma autenticação que estava a caminho.
 *
 * Resolve com { outcome: "authenticated" | "rejected" | "timeout" | "cancelled" }.
 */
export function pollChallengeStatus({
  username,
  token,
  sessionId,
  intervalSeconds = 3,
  timeoutSeconds = 300,
  shouldStop,
}) {
  const startedAt = Date.now();
  const intervalMs = Math.max(1, intervalSeconds) * 1000;
  const timeoutMs = Math.max(1, timeoutSeconds) * 1000;

  return new Promise((resolve) => {
    async function tick() {
      if (typeof shouldStop === "function" && shouldStop()) {
        resolve({ outcome: "cancelled" });
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolve({ outcome: "timeout" });
        return;
      }

      try {
        const response = await api.post("payment/picpay-3ds-status", { username, token, sessionId });
        const status = response?.data?.status;
        if (status === "authenticated") {
          resolve({ outcome: "authenticated" });
          return;
        }
        if (status === "rejected") {
          // A resposta leva a situacao do cartao (tentativas restantes, bloqueio), que a tela de
          // recusa mostra.
          resolve({ outcome: "rejected", data: response.data });
          return;
        }
      } catch (_) {
        // Segue tentando até o teto.
      }

      setTimeout(tick, intervalMs);
    }

    tick();
  });
}

/**
 * Limites de captura de postMessage. Propositalmente baixos: o campo `detail` do
 * endpoint de telemetria (payment/picpay-3ds-client-error) trunca em 500 chars.
 */
const OBSERVED_MAX_MESSAGES = 3;
const OBSERVED_MAX_CHARS = 160;

/** Reduz o payload de um postMessage a uma string curta, sem nunca lançar. */
function describeMessage(data) {
  try {
    const text = typeof data === "string" ? data : JSON.stringify(data);
    if (typeof text !== "string") return typeof data;
    return text.length > OBSERVED_MAX_CHARS ? text.slice(0, OBSERVED_MAX_CHARS) : text;
  } catch (_) {
    return "[unserializable " + typeof data + "]";
  }
}

/**
 * Registra TODA mensagem recebida na janela de espera, não só a que casa com o formato
 * esperado — é o que permite descobrir o contrato real publicado pelo iframe do parceiro
 * quando o formato esperado está errado (foi assim que o contrato do DDC foi descoberto).
 */
function recordMessage(observed, event, startedAt) {
  if (observed.length >= OBSERVED_MAX_MESSAGES) return;
  observed.push({
    origin: event.origin,
    ms: Date.now() - startedAt,
    data: describeMessage(event.data),
  });
}

/**
 * Reporta ao backend uma falha observada no desafio 3DS (timeout, cancelamento
 * do usuário, erro no iframe) para o mesmo pipeline de observabilidade (SendLog)
 * usado pelo restante do fluxo 3DS. Melhor esforço: nunca lança erro.
 */
export async function reportChallengeError(errorType, { username, token, merchantChargeId, chargeId, detail } = {}) {
  try {
    await api.post("payment/picpay-3ds-client-error", {
      username,
      token,
      merchantChargeId: merchantChargeId || "",
      chargeId: chargeId || "",
      errorType,
      detail: detail || "",
    });
  } catch (_) {
    // telemetria best-effort; uma falha aqui não pode afetar o checkout
  }
}

/**
 * Envia o JWT de challenge para o iframe visível via POST oculto.
 * O iframe precisa já estar renderizado no DOM com o name informado.
 */
export function submitChallengeForm(accessToken, challengeUrl, iframeName) {
  const existing = document.getElementById("picpay-3ds-challenge-form");
  if (existing) existing.remove();

  const form = document.createElement("form");
  form.id = "picpay-3ds-challenge-form";
  form.method = "POST";
  form.action = challengeUrl;
  form.target = iframeName;

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "JWT";
  input.value = accessToken;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();

  setTimeout(() => {
    const f = document.getElementById("picpay-3ds-challenge-form");
    if (f) f.remove();
  }, 1000);
}

/**
 * Observa, sem interferir, as mensagens que chegam enquanto o iframe do desafio está aberto.
 *
 * Não decide nada: quem determina o desfecho é o polling do servidor. Este observador existe
 * só para descobrir o contrato de postMessage do StepUp, que o PicPay não documenta em nenhuma
 * das suas páginas e que nunca foi observado — nenhum desafio ocorreu desde que o código existe,
 * e o sandbox não renderiza desafio. O primeiro desafio real é a única chance de capturá-lo.
 *
 * A versão anterior desta função tentava usar essa mensagem como sinal de conclusão, esperando
 * `ActionCode === "COMPLETE"` — um campo do Songbird, SDK que esta página não carrega. Nunca
 * disparou. Não vale repetir o erro de depender de um formato que não conhecemos.
 */
export function observeChallengeMessages() {
  const observed = [];
  const startedAt = Date.now();

  function onMessage(event) {
    recordMessage(observed, event, startedAt);
  }

  window.addEventListener("message", onMessage);

  return {
    stop() {
      window.removeEventListener("message", onMessage);
      return observed;
    },
  };
}


/**
 * Coleta fingerprint do dispositivo via iframe oculto do Cardinal Commerce (Cruise).
 * Resolve quando o iframe reportar conclusão ou após timeout de 12 segundos.
 */
export function collectDeviceFingerprint(accessToken, deviceDataCollectionUrl) {
  return new Promise((resolve) => {
    const TIMEOUT_MS = 12000;

    const observed = [];
    const startedAt = Date.now();

    const timeout = setTimeout(() => {
      cleanup();
      resolve({ completed: false, observed });
    }, TIMEOUT_MS);

    function onMessage(event) {
      recordMessage(observed, event, startedAt);
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        // Contrato real, capturado numa compra de produção em 08/09/2026:
        //   {"MessageType":"profile.completed","SessionId":"<referenceId>","Status":true}
        // O `ActionCode === "COMPLETE"` que estava aqui vem do Songbird, o SDK JS do
        // Cardinal -- que NÃO é carregado nesta página: a integração com o Cruise é feita
        // à mão, com form POST e postMessage cru. Por isso o listener nunca disparava e
        // toda compra de cartão pagava os 12s de timeout.
        // Sem filtro por event.origin de propósito: um filtro errado volta a quebrar em
        // silêncio, e o pior que uma mensagem forjada faz aqui é encerrar a espera antes.
        if (data?.MessageType === "profile.completed") {
          cleanup();
          resolve({ completed: data?.Status === true, observed });
        }
      } catch (_) {
        // ignore parse errors from unrelated messages
      }
    }

    function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      const iframe = document.getElementById("picpay-3ds-device-collect");
      if (iframe) iframe.remove();
      const form = document.getElementById("picpay-3ds-device-form");
      if (form) form.remove();
    }

    window.addEventListener("message", onMessage);

    // Hidden iframe that will receive the device collection response
    const iframe = document.createElement("iframe");
    iframe.id = "picpay-3ds-device-collect";
    iframe.name = "picpay-3ds-device-collect";
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    // Form POSTing the JWT to the device data collection URL inside the iframe
    const form = document.createElement("form");
    form.id = "picpay-3ds-device-form";
    form.method = "POST";
    form.action = deviceDataCollectionUrl;
    form.target = "picpay-3ds-device-collect";

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "JWT";
    input.value = accessToken;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  });
}
